import { useCallback } from 'react';
import { useDebriefSelector } from 'ff/debrief/hooks/index';
import {
    Accordions,
    AirportOptions,
    BooleanToggleEvent,
    NavlevelToggleEvent,
} from 'ff/debrief/models';
import {
    toggleAccordion,
    toggleAirportType,
    toggleAirspaceSetting,
    toggleCulturalSetting,
    toggleMapSetting,
    toggleShowAirports,
    resetMapBounds,
    setMapMode,
} from 'ff/debrief/features/MapSlice';
import { useDebriefDispatch } from 'ff/debrief/hooks';

export const useMapSettings = () => {
    const dispatch = useDebriefDispatch();
    const mapMode = useDebriefSelector(s => s.mapSlice.mapMode);
    const currentSettings = useDebriefSelector(s => s.mapSlice.settings);
    const settingsUI = useDebriefSelector(s => s.mapSlice.settingsUI);
    const boundsPadding = useDebriefSelector(s => s.mapSlice.boundsPadding);

    const toggleMapMode = useCallback(() => {
        dispatch(setMapMode(mapMode === '2D' ? '3D' : '2D'));
    }, [dispatch]);

    const toggleAccordionCallback = useCallback(
        (accordionSection: Accordions) => {
            dispatch(toggleAccordion(accordionSection));
        },
        [dispatch]
    );

    const toggleCulturalSettingCallback = useCallback(
        (setting: string) => {
            dispatch(toggleCulturalSetting(setting));
        },
        [dispatch]
    );

    const toggleMapSettingCallback = useCallback(
        (toggleEvent: BooleanToggleEvent | NavlevelToggleEvent) => {
            dispatch(toggleMapSetting(toggleEvent));
        },
        [dispatch]
    );

    const toggleAirspaceSettingCallback = useCallback(
        (setting: string) => {
            dispatch(toggleAirspaceSetting(setting));
        },
        [dispatch]
    );

    const toggleAirports = useCallback(() => {
        dispatch(toggleShowAirports());
    }, [dispatch]);

    const toggleAirportTypeCallback = useCallback(
        (setting: AirportOptions) => {
            dispatch(toggleAirportType(setting));
        },
        [dispatch]
    );

    const resetMap = useCallback(() => {
        dispatch(resetMapBounds());
    }, [dispatch]);

    const allAirspaceDisabled = () => {
        return Object.values(currentSettings.airspaceFilters).every(
            item => item === false
        );
    };
    /**
     * Hacky way to remove aero layer from the dom if all the relevant settings
     * are disabled.
     */
    const allAeroLayersDisabled = () => {
        return (
            !currentSettings.airports &&
            (!currentSettings.airspace ||
                (currentSettings.airspace && allAirspaceDisabled())) &&
            !currentSettings?.filters?.navaids &&
            !currentSettings?.filters?.waypoints &&
            !currentSettings?.filters?.airways
        );
    };

    return {
        mapMode,
        currentSettings,
        settingsUI,
        boundsPadding,
        allAeroLayersDisabled,
        toggleMapMode: toggleMapMode,
        toggleAccordion: toggleAccordionCallback,
        toggleCulturalSetting: toggleCulturalSettingCallback,
        toggleMapSetting: toggleMapSettingCallback,
        toggleAirspaceSetting: toggleAirspaceSettingCallback,
        toggleShowAirports: toggleAirports,
        toggleAirportType: toggleAirportTypeCallback,
        resetMap: resetMap,
    };
};
